/**
 * Invoice arithmetic, document metadata, and the shape of a blank document.
 *
 * ── This file is a preview, not the authority ───────────────────────────────
 * Every figure an invoice is *stored* with is computed by recalc_invoice_totals()
 * in migration 17, inside the same transaction as the save. What is here exists
 * so the operator sees the total move while they type, before anything is
 * written. After a save the panel re-reads what the database stored and shows
 * that instead.
 *
 * The two therefore have to agree, and the SQL is written to the identical
 * formula in the identical order. If you change one, change the other — and the
 * order matters as much as the formula, because rounding per line and then
 * summing does not give the same answer as summing and then rounding.
 */

// ---------------------------------------------------------------------------
//  Document kinds
// ---------------------------------------------------------------------------
//  What the heading says, how the number is prefixed, and whether the document
//  represents money actually owed. `owed` is what the list page totals up: a
//  quote is not revenue and counting it as such would flatter the figures.
// ---------------------------------------------------------------------------
export const KINDS = {
  invoice: {
    label: 'Invoice',
    heading: 'Tax Invoice',
    headingNoVat: 'Invoice',
    prefix: 'INV',
    owed: true,
    blurb: 'Money is owed. Use this once goods or work have been supplied.',
  },
  quote: {
    label: 'Quote',
    heading: 'Quotation',
    headingNoVat: 'Quotation',
    prefix: 'QUO',
    owed: false,
    blurb: 'An offer with a price. Nothing is owed until it is accepted.',
  },
  proforma: {
    label: 'Pro forma',
    heading: 'Pro Forma Invoice',
    headingNoVat: 'Pro Forma Invoice',
    prefix: 'PRO',
    owed: true,
    blurb: 'A request for payment before supply — a deposit or a special order.',
  },
  credit_note: {
    label: 'Credit note',
    heading: 'Credit Note',
    headingNoVat: 'Credit Note',
    prefix: 'CRN',
    owed: false,
    blurb: 'Reverses an invoice already issued, in whole or in part.',
  },
}

export const KIND_ORDER = ['invoice', 'quote', 'proforma', 'credit_note']

export const STATUSES = {
  draft: { label: 'Draft', hint: 'Not sent. Safe to change.' },
  sent: { label: 'Sent', hint: 'Issued to the customer and awaiting payment.' },
  paid: { label: 'Paid', hint: 'Settled in full.' },
  cancelled: { label: 'Cancelled', hint: 'Voided. Kept so the numbering has no hole in it.' },
}

/**
 * The heading a document prints.
 *
 * "Tax Invoice" is a regulated term in South Africa: section 20 of the VAT Act
 * allows it only where the supplier is VAT registered, and the customer cannot
 * claim input VAT from a document that carries the words without a VAT number.
 * So when no VAT number is set, the heading quietly drops back to "Invoice"
 * rather than making a claim the business is not entitled to make.
 */
export function documentHeading(kind, vatNumber) {
  const meta = KINDS[kind] ?? KINDS.invoice
  return vatNumber ? meta.heading : meta.headingNoVat
}

// ---------------------------------------------------------------------------
//  Money
// ---------------------------------------------------------------------------

/**
 * Round to cents the way Postgres `numeric` does: half away from zero.
 *
 * Neither half of that is what JavaScript gives you for free, and both matter:
 *
 *   Half, not nearly-half.  Postgres holds 3 × 3.335 as exactly 10.005 and
 *   rounds it to 10.01. A double holds it as 10.004999999999999 and rounds it
 *   to 10.00 — one cent adrift from the figure the database will store and the
 *   customer will be charged. The nudge below is a relative tolerance of 1e-12,
 *   four orders of magnitude above double precision's own error (~1e-16) and
 *   many orders below any distinction that could mean money.
 *
 *   Away from zero, not toward positive infinity.  Math.round(-0.5) is -0;
 *   Postgres rounds -0.005 to -0.01. Credit note lines are negative, so getting
 *   this wrong would quietly under-credit a return.
 */
export const cents = (value) => {
  const n = Number(value)
  if (!Number.isFinite(n)) return 0
  const scaled = Math.abs(n) * 100
  const nudged = scaled + Math.max(scaled, 1) * 1e-12
  return (Math.sign(n) * Math.floor(nudged + 0.5)) / 100
}

/**
 * Every figure on a document, from its lines and its settings.
 *
 * Mirrors recalc_invoice_totals() exactly:
 *
 *   subtotal  = Σ round(qty × price)      ← per line, then summed
 *   discount  = none | % of subtotal | fixed, capped at the subtotal
 *   net       = subtotal − discount + shipping
 *   inclusive : tax = net × rate ÷ (100 + rate),  total = net
 *   exclusive : tax = net × rate ÷ 100,           total = net + tax
 *
 * The inclusive branch pulls the VAT back out of a price that already contains
 * it — dividing by 115 at the standard rate, not by 100. Dividing by 100 there
 * is the commonest VAT mistake there is, and it overstates the tax every time.
 */
export function computeTotals({
  items = [],
  discountType = 'none',
  discountValue = 0,
  shippingAmount = 0,
  taxRate = 15,
  pricesIncludeTax = true,
} = {}) {
  const lines = items.map((item) => ({
    ...item,
    lineTotal: cents(Number(item.quantity || 0) * Number(item.unit_price || 0)),
  }))

  const subtotal = cents(lines.reduce((sum, line) => sum + line.lineTotal, 0))

  let discount = 0
  if (discountType === 'percent') discount = cents((subtotal * Number(discountValue || 0)) / 100)
  else if (discountType === 'amount') discount = cents(Number(discountValue || 0))
  discount = Math.min(Math.max(discount, 0), Math.max(subtotal, 0))

  const shipping = cents(shippingAmount)
  const net = cents(subtotal - discount + shipping)
  const rate = Number(taxRate) || 0

  const tax = pricesIncludeTax
    ? cents((net * rate) / (100 + rate))
    : cents((net * rate) / 100)

  const total = pricesIncludeTax ? net : cents(net + tax)

  return {
    lines,
    subtotal,
    discountAmount: discount,
    shippingAmount: shipping,
    taxAmount: tax,
    total,
    // What the lines come to before VAT. Printed only on an exclusive document,
    // where it is the column the customer checks the arithmetic against.
    netBeforeTax: pricesIncludeTax ? cents(net - tax) : net,
    lineCount: lines.length,
  }
}

// ---------------------------------------------------------------------------
//  Defaults
// ---------------------------------------------------------------------------

const isoDate = (date) => {
  // Africa/Johannesburg is UTC+2 with no daylight saving, so the local calendar
  // date is what toLocaleDateString gives with an explicit zone. Using
  // toISOString() here would put an invoice raised at 01:00 on the day before.
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Johannesburg',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date)
  const get = (type) => parts.find((p) => p.type === type).value
  return `${get('year')}-${get('month')}-${get('day')}`
}

export const today = () => isoDate(new Date())

export const addDays = (dateString, days) => {
  const d = new Date(`${dateString}T12:00:00Z`) // midday, so no zone can shift the date
  d.setUTCDate(d.getUTCDate() + Number(days || 0))
  return d.toISOString().slice(0, 10)
}

/** A new, empty document, pre-filled from the shop's settings. */
export function blankInvoice(settings = {}) {
  const start = today()
  return {
    kind: 'invoice',
    status: 'draft',
    supplier_id: null,
    customer_id: null,
    order_id: null,
    bill_to_name: '',
    bill_to_contact: '',
    bill_to_email: '',
    bill_to_phone: '',
    bill_to_vat_number: '',
    bill_to_reg_number: '',
    bill_to_address: '',
    bill_to_city: '',
    bill_to_province: '',
    bill_to_postal_code: '',
    bill_to_country: 'South Africa',
    issue_date: start,
    due_date: addDays(start, settings.invoice_due_days ?? 30),
    reference: '',
    notes: '',
    terms: settings.invoice_terms ?? '',
    internal_notes: '',
    currency: 'ZAR',
    prices_include_tax: settings.invoice_prices_include_tax ?? true,
    tax_rate: settings.invoice_tax_rate ?? 15,
    discount_type: 'none',
    discount_value: 0,
    shipping_amount: 0,
    amount_paid: 0,
  }
}

export const blankItem = () => ({
  key: `new-${Math.random().toString(36).slice(2, 10)}`,
  product_id: null,
  description: '',
  sku: '',
  detail: '',
  quantity: 1,
  unit: '',
  unit_price: '',
})

/**
 * The issuing business, from settings, with the shop's real details as the
 * fallback.
 *
 * Falling back rather than showing blanks matters: an invoice with no address on
 * it is not a valid tax invoice, and a shop that has not opened the Settings
 * page yet should still be able to send something correct.
 */
export function issuerFromSettings(settings = {}) {
  return {
    name: settings.invoice_business_name || "Coquis Men's Outfitters",
    vatNumber: settings.invoice_business_vat || '',
    regNumber: settings.invoice_business_reg || '',
    address:
      settings.invoice_business_address ||
      '295 Galway Avenue\nGalway Centre, Bronberrick\nCenturion, 0157',
    phone: settings.invoice_business_phone || '012 660 2104',
    email: settings.invoice_business_email || 'info@coquis.co.za',
    bankDetails: settings.invoice_bank_details || '',
  }
}

/** Settings rows → a plain object keyed by setting name. */
export const settingsMap = (rows = []) =>
  Object.fromEntries((rows ?? []).map((row) => [row.key, row.value]))

/**
 * Is this document overdue?
 *
 * Computed, never stored — the same rule the admin_invoices view applies. A
 * stored flag needs something to run nightly to keep it true, and the night it
 * does not run the shop chases the wrong people.
 */
export function isOverdue(invoice) {
  if (!invoice || invoice.status !== 'sent' || !invoice.due_date) return false
  if (!KINDS[invoice.kind]?.owed) return false
  return invoice.due_date < today()
}
